import React from 'react';
import PropTypes from 'prop-types';
import queryString from 'querystring';
import styled from 'styled-components';

import { AppContext } from '@prisjakt/ui-app-context';
import { bemGenerator } from '@prisjakt/ui-base';
import CloseIcon from 'mdi-react/CloseCircleOutlineIcon';
import cn from 'classnames';

import './index.scss';

const styles = bemGenerator('pj-ui-search-bar', [
  'desktop-input',
  'input',
  'inputwrapper',
  'clear',
  'abort',
  'button',
  'full-search-icon',
  'clear-icon',
]);

const Close = styled(CloseIcon).attrs(p => ({
  color: p.theme.icons.tertiary,
}))``;

class SearchBar extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      initialQuery: this.getInitialQuery(),
    };

    this.inputChange = this.props.onInputChange;
  }

  getInitialQuery() {
    const { search } = this.props;
    const qs = queryString.parse(search.substr(1));

    return qs.search || qs.q || '';
  }

  onTextChange(e, query) {
    if (e) {
      const input = e.currentTarget;
      const otherInput =
        input === this.desktopInput ? this.mobileInput : this.desktopInput;

      if (otherInput) {
        otherInput.value = query;
      }
    }

    this.inputChange(query);
  }

  getInput() {
    if (this.mobileInput && this.mobileInput.offsetParent) {
      return this.mobileInput;
    }

    if (this.desktopInput && this.desktopInput.offsetParent) {
      return this.desktopInput;
    }

    return {
      focus: () => {},
      blur: () => {},
      value: '',
    };
  }

  hasQuery() {
    return this.getInput().value;
  }

  focusInput() {
    return this.getInput().focus();
  }

  clearInputs(trigger) {
    [this.mobileInput, this.desktopInput].forEach(i => {
      if (!i) {
        return;
      }
      i.value = ''; // eslint-disable-line no-param-reassign
      i.blur();
    });
    if (trigger) {
      this.onTextChange(undefined, '');
    }
    this.focusInput();
  }

  searchParams() {
    const { t } = this.context;
    const placeholderText = t('search', 'search.placeholder');

    const { initialQuery, selected } = this.state;

    return {
      'aria-expanded': this.props.focused,
      'aria-owns': 'overlaySearchResults',
      'aria-autocomplete': 'both',
      'aria-activedescendant': selected ? `selection${selected}` : '',
      autoCorrect: 'off',
      autoCapitalize: 'off',
      spellCheck: 'false',
      type: 'search',
      placeholder: placeholderText,
      defaultValue: initialQuery,
      onFocus: e => {
        this.props.onFocusChange();
        this.onTextChange(e, e.target.value);
      },
      onBlur: () => {
        this.props.onBlurChange();
      },
      onChange: e => this.onTextChange(e, e.target.value),
    };
  }

  render() {
    const searchParams = this.searchParams();
    const { mobile, className, autoFocusOnExpand, autoFocus } = this.props;

    return (
      <div data-searchbar="true" className={cn(styles.block, className)}>
        <div className={styles.inputwrapper}>
          {!mobile && (
            <input
              {...searchParams}
              aria-describedby="desktopSearchInstr"
              ref={input => {
                this.desktopInput = input;
                this.mobileInput = input;
              }}
              className={styles['desktop-input']}
              data-suggestions="search"
              autoFocus={autoFocus} // eslint-disable-line jsx-a11y/no-autofocus
            />
          )}

          {mobile && (
            <input
              {...searchParams}
              ref={input => {
                this.mobileInput = input;
              }}
              className={styles.input}
              autoFocus={autoFocusOnExpand} // eslint-disable-line jsx-a11y/no-autofocus
              data-suggestions="search"
            />
          )}

          <button
            type="button"
            className={styles['clear-icon'].withModifier({
              show: this.getInput().value,
            })}
            onClick={e => {
              this.props.onClear();
              this.clearInputs(e);
            }}
          >
            <Close size={20} />
          </button>
        </div>
      </div>
    );
  }
}

SearchBar.propTypes = {
  initialQuery: PropTypes.string,
  placeholderText: PropTypes.string,
  autoFocus: PropTypes.bool,
  autoFocusOnExpand: PropTypes.bool,
  focused: PropTypes.bool,
  className: PropTypes.string,
  onFocusChange: PropTypes.func,
  onInputChange: PropTypes.func,
  onClear: PropTypes.func,
  selected: PropTypes.number,
  search: PropTypes.string.isRequired,
  mobile: PropTypes.bool,
};

SearchBar.defaultProps = {
  initialQuery: '',
  placeholderText: '',
  selected: undefined,
  autoFocus: false,
  autoFocusOnExpand: false,
  focused: false,
  className: undefined,
  onClear: () => {},
  onFocusChange: () => {},
  onInputChange: () => {},
  mobile: false,
};

SearchBar.contextType = AppContext;

export default SearchBar;
